import FormattedDatePickerReportsV3 from "../FormattedDatePickerReportsV3";
import { useEditorEnumerations } from "../hooks/useEditorEnumerations";
import { ISO_DATE_PATTERN, PASS_THROUGH_FUNCTIONS } from "../types/constants";
import { useColumnFilter } from "../utils/columnSelectorUtils";
import {
  comparisonLiteralAffixes,
  comparisonLiteralMultiplier,
  rescaleComparisonLiterals,
  savedLiteral,
  shownLiteral,
} from "../utils/comparisonLiteralUtils";
import {
  useAggregatorData,
  normalizeDataType,
  isEmpty,
  isLiteral,
} from "../utils/expressionEditorUtils";
import {
  columnDropAllowed,
  enumerationLabel,
  enumFunctionAllowed,
  inOperandConstraints,
  intersectSlotTypes,
  pairOperandConstraints,
  passThroughConstraints,
} from "../utils/functionPairingUtils";
import {
  ignoreBackdropClose,
  pendingLiteralValue,
} from "../utils/literalEditUtils";
import ColumnFilterBar, { TYPE_COLORS } from "./ColumnFilterBar";
import DataTypeIcon from "./DataTypeIcon";
import EnumerationPickerItems from "./EnumerationPickerItems";
import LiteralEditActions from "./LiteralEditActions";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDrag, useDrop } from "react-dnd";

const AGG_DRAG_TYPE = "AGG_COLUMN";

const EditorContext = createContext({
  availableColumns: [],
  simpleFuncs: [],
  pairFuncs: [],
  unaryFuncs: [],
  noParamsFuncs: [],
  columnFuncs: [],
  simpleDisplay: {},
  pairDisplay: {},
  pairLabel: {},
  unaryDisplay: {},
  noParamsDisplay: {},
  columnDisplay: {},
  funcValidFor: {},
  funcProduces: {},
  columnValidFor: {},
  funcPairingType: {},
  funcEnumerations: {},
  columnEnumerations: {},
});

const typesCompatible = (produces, acceptedTypes) =>
  !produces?.length ||
  !acceptedTypes?.length ||
  produces.some((t) => acceptedTypes.includes(t));

const NODE_SX = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  px: 1,
  py: 0.5,
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  flexShrink: 0,
};

const SX_HEADING = { fontSize: "1.2rem", fontWeight: 400 };

const EmptyNode = ({
  onChange,
  acceptedTypes = [],
  requiredEnumerations,
  literalMultiplier = 1,
}) => {
  const {
    simpleFuncs,
    simpleDisplay,
    pairFuncs,
    pairLabel,
    pairDisplay,
    unaryFuncs,
    unaryDisplay,
    noParamsFuncs,
    noParamsDisplay,
    columnFuncs,
    columnDisplay,
    funcProduces,
    funcEnumerations,
    columnEnumerations,
  } = useContext(EditorContext);
  const nodeRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuStringValue, setMenuStringValue] = useState("");
  const [menuNumberValue, setMenuNumberValue] = useState(null);
  const [menuDateValue, setMenuDateValue] = useState("");

  const hasEnums =
    Array.isArray(requiredEnumerations) && requiredEnumerations.length > 0;
  const funcAllowed = (f) =>
    typesCompatible(funcProduces[f], acceptedTypes) &&
    enumFunctionAllowed(f, requiredEnumerations, {
      funcProduces,
      funcEnumerations,
    });
  const visibleSimpleFuncs = simpleFuncs.filter(funcAllowed);
  const visiblePairFuncs = pairFuncs.filter(funcAllowed);
  const visibleUnaryFuncs = unaryFuncs.filter(funcAllowed);
  const visibleNoParamsFuncs = noParamsFuncs.filter(funcAllowed);
  const visibleColumnFuncs = columnFuncs.filter(funcAllowed);
  const showIn = typesCompatible(["BOOL"], acceptedTypes);
  const showStringLiteral = typesCompatible(["STRING", "ENUM"], acceptedTypes);
  const showNumberLiteral = typesCompatible(["INT", "FLOAT"], acceptedTypes);
  const showDateLiteral = typesCompatible(["DATE"], acceptedTypes);
  const showLiteralSection =
    showStringLiteral || showNumberLiteral || showDateLiteral;
  const showLiteralEntry = !hasEnums && showLiteralSection;
  const pendingLiteral = pendingLiteralValue({
    showString: showStringLiteral,
    showNumber: showNumberLiteral,
    showDate: showDateLiteral,
    stringValue: menuStringValue,
    numberValue: menuNumberValue,
    dateValue: menuDateValue,
  });

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: AGG_DRAG_TYPE,
      canDrop: (item) =>
        columnDropAllowed(
          item,
          acceptedTypes,
          requiredEnumerations,
          columnEnumerations
        ),
      drop: (item) => onChange({ func: "VAR", name: item.internalName }),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [onChange, acceptedTypes, requiredEnumerations, columnEnumerations]
  );

  const combinedRef = (node) => {
    nodeRef.current = node;
    drop(node);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setMenuStringValue("");
    setMenuNumberValue(null);
    setMenuDateValue("");
  };

  const saveLiteral = () => {
    if (pendingLiteral === undefined) return;
    onChange(savedLiteral(pendingLiteral, literalMultiplier));
    closeMenu();
  };

  return (
    <>
      <Box
        ref={combinedRef}
        onClick={() => {
          setMenuStringValue("");
          setMenuNumberValue(null);
          setMenuDateValue("");
          setMenuOpen(true);
        }}
        sx={{
          width: 20,
          height: 20,
          border: "1px solid",
          borderColor: isOver
            ? canDrop
              ? "primary.main"
              : "error.main"
            : "text.disabled",
          borderRadius: 0.5,
          cursor: "pointer",
          backgroundColor: isOver
            ? canDrop
              ? "action.hover"
              : "error.light"
            : "transparent",
          flexShrink: 0,
          opacity: isOver && !canDrop ? 0.5 : 1,
          "&:hover": { borderColor: "text.secondary" },
        }}
      />
      <Menu
        anchorEl={nodeRef.current}
        open={menuOpen}
        onClose={showLiteralEntry ? ignoreBackdropClose(closeMenu) : closeMenu}
        slotProps={{
          paper: {
            sx: {
              maxHeight: "calc((100vh - 96px) * 0.7)",
              minHeight: 500,
            },
          },
        }}
      >
        {hasEnums && (
          <EnumerationPickerItems
            enumerations={requiredEnumerations}
            onSelect={(v) => {
              onChange(v);
              closeMenu();
            }}
          />
        )}
        {!hasEnums && showLiteralSection && (
          <>
            <ListSubheader disableSticky>{"Literal"}</ListSubheader>
            {showStringLiteral && (
              <Box
                sx={{ px: 1, py: 0.5 }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <TextField
                  autoFocus={showStringLiteral}
                  size={"small"}
                  label={"String"}
                  value={menuStringValue}
                  onChange={(e) => setMenuStringValue(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter") {
                      onChange(menuStringValue);
                      closeMenu();
                    }
                    if (e.key === "Escape") closeMenu();
                  }}
                />
              </Box>
            )}
            {showNumberLiteral && (
              <Box
                sx={{ px: 1, py: 0.5 }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <PrettyNumberTextField
                  autoFocus={!showStringLiteral && showNumberLiteral}
                  size={"small"}
                  label={"Number"}
                  variant={"outlined"}
                  value={menuNumberValue}
                  onChangeNumber={(n) => setMenuNumberValue(n)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter" && menuNumberValue !== null) {
                      onChange(
                        savedLiteral(menuNumberValue, literalMultiplier)
                      );
                      closeMenu();
                    }
                    if (e.key === "Escape") closeMenu();
                  }}
                />
              </Box>
            )}
            {showDateLiteral && (
              <Box
                sx={{ px: 1, py: 0.5 }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") saveLiteral();
                }}
              >
                <FormattedDatePickerReportsV3
                  compact
                  clearable={false}
                  value={menuDateValue || null}
                  onChangeDate={(iso) => setMenuDateValue(iso ?? "")}
                  sx={{ maxWidth: "92%" }}
                />
              </Box>
            )}
            {showLiteralEntry && (
              <Box sx={{ px: 1 }} onKeyDown={(e) => e.stopPropagation()}>
                <LiteralEditActions
                  onSave={saveLiteral}
                  onCancel={closeMenu}
                  saveDisabled={pendingLiteral === undefined}
                />
              </Box>
            )}
          </>
        )}
        {visibleSimpleFuncs.length > 0 && (
          <>
            <Divider />
            <ListSubheader disableSticky>{"List Functions"}</ListSubheader>
            {visibleSimpleFuncs.map((f) => (
              <MenuItem
                dense
                key={f}
                onClick={() => {
                  onChange({ func: f, values: [] });
                  closeMenu();
                }}
              >
                {simpleDisplay[f]}
              </MenuItem>
            ))}
          </>
        )}
        {visiblePairFuncs.length > 0 && (
          <>
            <Divider />
            <ListSubheader disableSticky>{"Operators"}</ListSubheader>
            {visiblePairFuncs.map((f) => (
              <MenuItem
                dense
                key={f}
                onClick={() => {
                  onChange({ func: f, a: {}, b: {} });
                  closeMenu();
                }}
              >
                {`${pairLabel[f]} (${pairDisplay[f]})`}
              </MenuItem>
            ))}
          </>
        )}
        {visibleNoParamsFuncs.length > 0 && (
          <>
            <Divider />
            <ListSubheader disableSticky>{"Constant Functions"}</ListSubheader>
            {visibleNoParamsFuncs.map((f) => (
              <MenuItem
                dense
                key={f}
                onClick={() => {
                  onChange({ func: f });
                  closeMenu();
                }}
              >
                {noParamsDisplay[f]}
              </MenuItem>
            ))}
          </>
        )}
        <>
          <Divider />
          <ListSubheader disableSticky>{"Other Functions"}</ListSubheader>
          <MenuItem
            dense
            onClick={() => {
              onChange({ func: "CASE", cases: [], otherwise: {} });
              closeMenu();
            }}
          >
            {"Case"}
          </MenuItem>
          {showIn && (
            <MenuItem
              dense
              onClick={() => {
                onChange({ func: "IN", value: {}, params: [] });
                closeMenu();
              }}
            >
              {"In"}
            </MenuItem>
          )}
          {visibleUnaryFuncs.map((f) => (
            <MenuItem
              dense
              key={f}
              onClick={() => {
                onChange({ func: f, value: {} });
                closeMenu();
              }}
            >
              {unaryDisplay[f]}
            </MenuItem>
          ))}
        </>
        {visibleColumnFuncs.length > 0 && (
          <>
            <Divider />
            <ListSubheader disableSticky>{"Column Functions"}</ListSubheader>
            {visibleColumnFuncs.map((f) => (
              <MenuItem
                dense
                key={f}
                onClick={() => {
                  onChange({ func: f, name: null });
                  closeMenu();
                }}
              >
                {columnDisplay[f]}
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
    </>
  );
};

const SX_DELETE = {
  fontSize: "0.8rem",
  cursor: "pointer",
  color: "text.disabled",
  ml: 0.5,
  flexShrink: 0,
  "&:hover": { color: "text.secondary" },
};

const LiteralNode = ({
  exp,
  onChange,
  requiredEnumerations,
  literalMultiplier = 1,
  literalAffixes = { prefix: "", suffix: "" },
}) => {
  const nodeRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [editString, setEditString] = useState("");
  const [editNumber, setEditNumber] = useState(null);

  const isDate = typeof exp === "string" && ISO_DATE_PATTERN.test(exp);
  const hasEnums =
    Array.isArray(requiredEnumerations) && requiredEnumerations.length > 0;
  const enumLabel = enumerationLabel(exp, requiredEnumerations);

  const label =
    enumLabel ??
    (typeof exp === "string"
      ? `"${exp}"`
      : typeof exp === "boolean"
      ? exp
        ? "true"
        : "false"
      : `${literalAffixes.prefix}${shownLiteral(
          exp,
          literalMultiplier
        ).toLocaleString()}${literalAffixes.suffix}`);

  const openEdit = () => {
    if (typeof exp === "string") setEditString(exp);
    if (typeof exp === "number")
      setEditNumber(shownLiteral(exp, literalMultiplier));
    setEditing(true);
  };

  const closeEdit = () => setEditing(false);

  const editsText =
    !hasEnums && (typeof exp === "number" || typeof exp === "string");

  return (
    <>
      <Box
        ref={nodeRef}
        sx={{ ...NODE_SX, cursor: "pointer" }}
        onClick={openEdit}
      >
        <Typography variant={"body2"} color={"text.secondary"}>
          {"Literal"}
        </Typography>
        <Typography variant={"body2"}>{label}</Typography>
        <CloseIcon
          sx={SX_DELETE}
          onClick={(e) => {
            e.stopPropagation();
            onChange({});
          }}
        />
      </Box>
      <Menu
        anchorEl={nodeRef.current}
        open={editing}
        onClose={editsText ? ignoreBackdropClose(closeEdit) : closeEdit}
      >
        {hasEnums && (
          <EnumerationPickerItems
            enumerations={requiredEnumerations}
            selected={exp}
            onSelect={(v) => {
              onChange(v);
              closeEdit();
            }}
          />
        )}
        {!hasEnums && typeof exp === "number" && (
          <Box sx={{ px: 1, py: 0.5 }} onKeyDown={(e) => e.stopPropagation()}>
            <PrettyNumberTextField
              autoFocus
              size={"small"}
              label={"Number"}
              variant={"outlined"}
              value={editNumber}
              onChangeNumber={(n) => setEditNumber(n)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter" && editNumber !== null) {
                  onChange(savedLiteral(editNumber, literalMultiplier));
                  closeEdit();
                }
                if (e.key === "Escape") closeEdit();
              }}
            />
            <LiteralEditActions
              onSave={() => {
                onChange(savedLiteral(editNumber, literalMultiplier));
                closeEdit();
              }}
              onCancel={closeEdit}
              saveDisabled={editNumber === null}
            />
          </Box>
        )}
        {!hasEnums && typeof exp === "string" && isDate && (
          <Box
            sx={{ px: 1, py: 0.5 }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter" && editString) {
                onChange(editString);
                closeEdit();
              }
            }}
          >
            <FormattedDatePickerReportsV3
              compact
              clearable={false}
              value={editString || null}
              onChangeDate={(iso) => setEditString(iso ?? "")}
              sx={{ maxWidth: "92%" }}
            />
            <LiteralEditActions
              onSave={() => {
                onChange(editString);
                closeEdit();
              }}
              onCancel={closeEdit}
              saveDisabled={!editString}
            />
          </Box>
        )}
        {!hasEnums && typeof exp === "string" && !isDate && (
          <Box sx={{ px: 1, py: 0.5 }} onKeyDown={(e) => e.stopPropagation()}>
            <TextField
              autoFocus
              size={"small"}
              label={"String"}
              value={editString}
              onChange={(e) => setEditString(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  onChange(editString);
                  closeEdit();
                }
                if (e.key === "Escape") closeEdit();
              }}
            />
            <LiteralEditActions
              onSave={() => {
                onChange(editString);
                closeEdit();
              }}
              onCancel={closeEdit}
            />
          </Box>
        )}
        {!hasEnums && typeof exp === "boolean" && (
          <>
            <MenuItem
              dense
              onClick={() => {
                onChange(true);
                closeEdit();
              }}
            >
              {"true"}
            </MenuItem>
            <MenuItem
              dense
              onClick={() => {
                onChange(false);
                closeEdit();
              }}
            >
              {"false"}
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

const VarNode = ({ exp, onChange }) => {
  const { availableColumns } = useContext(EditorContext);
  const col = availableColumns.find((c) => c.internalName === exp.name);
  const displayName = col?.displayName ?? exp.name;
  return (
    <Box sx={NODE_SX}>
      <Typography variant={"body2"} color={"text.secondary"}>
        {"column"}
      </Typography>
      <Typography variant={"body2"}>{`"${displayName}"`}</Typography>
      <CloseIcon sx={SX_DELETE} onClick={() => onChange({})} />
    </Box>
  );
};

// Forward declaration — assigned after SimpleFuncNode and PairFuncNode are defined
let ExpressionNode;

const SimpleFuncNode = ({
  exp,
  onChange,
  acceptedTypes,
  requiredEnumerations,
}) => {
  const {
    simpleDisplay,
    funcValidFor,
    funcProduces,
    availableColumns,
    columnEnumerations,
    funcEnumerations,
  } = useContext(EditorContext);
  const displayValues = [...exp.values, {}];
  const childConstraints = PASS_THROUGH_FUNCTIONS.includes(exp.func)
    ? passThroughConstraints(
        displayValues,
        intersectSlotTypes(acceptedTypes, funcValidFor[exp.func]),
        requiredEnumerations,
        { funcProduces, availableColumns, columnEnumerations, funcEnumerations }
      )
    : displayValues.map(() => ({
        acceptedTypes: funcValidFor[exp.func] ?? [],
      }));

  const handleChildChange = (i, newChild) => {
    if (i < exp.values.length) {
      if (isEmpty(newChild)) {
        onChange({ ...exp, values: exp.values.filter((_, j) => j !== i) });
      } else {
        onChange({
          ...exp,
          values: exp.values.map((v, j) => (j === i ? newChild : v)),
        });
      }
    } else {
      onChange({ ...exp, values: [...exp.values, newChild] });
    }
  };

  return (
    <Box sx={{ ...NODE_SX, alignItems: "flex-start", p: 1 }}>
      <Typography variant={"body2"} sx={{ fontWeight: 500, pt: 0.5, pr: 0.5 }}>
        {simpleDisplay[exp.func]}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {displayValues.map((child, i) => (
          <ExpressionNode
            key={i}
            exp={child}
            onChange={(newChild) => handleChildChange(i, newChild)}
            acceptedTypes={childConstraints[i].acceptedTypes ?? []}
            requiredEnumerations={childConstraints[i].requiredEnumerations}
          />
        ))}
      </Box>
      <CloseIcon sx={{ ...SX_DELETE, mt: 0.5 }} onClick={() => onChange({})} />
    </Box>
  );
};

const PairFuncNode = ({ exp, onChange }) => {
  const {
    pairDisplay,
    funcValidFor,
    funcPairingType,
    funcProduces,
    availableColumns,
    columnEnumerations,
    funcEnumerations,
  } = useContext(EditorContext);
  const constraints = pairOperandConstraints(
    exp.a,
    exp.b,
    {
      pairingType: funcPairingType[exp.func],
      validFor: funcValidFor[exp.func] ?? [],
    },
    { funcProduces, availableColumns, columnEnumerations, funcEnumerations }
  );
  const changeSide = (side, value) =>
    onChange(
      rescaleComparisonLiterals(
        exp,
        { ...exp, [side]: value },
        availableColumns
      )
    );
  return (
    <Box sx={{ ...NODE_SX, p: 1, gap: 1 }}>
      <ExpressionNode
        exp={exp.a}
        onChange={(newA) => changeSide("a", newA)}
        acceptedTypes={constraints.a.acceptedTypes ?? []}
        requiredEnumerations={constraints.a.requiredEnumerations}
        literalMultiplier={comparisonLiteralMultiplier(
          exp.func,
          exp.b,
          availableColumns
        )}
        literalAffixes={comparisonLiteralAffixes(
          exp.func,
          exp.b,
          availableColumns
        )}
      />
      <Typography variant={"body2"} sx={{ fontWeight: 600, px: 0.5 }}>
        {pairDisplay[exp.func]}
      </Typography>
      <ExpressionNode
        exp={exp.b}
        onChange={(newB) => changeSide("b", newB)}
        acceptedTypes={constraints.b.acceptedTypes ?? []}
        requiredEnumerations={constraints.b.requiredEnumerations}
        literalMultiplier={comparisonLiteralMultiplier(
          exp.func,
          exp.a,
          availableColumns
        )}
        literalAffixes={comparisonLiteralAffixes(
          exp.func,
          exp.a,
          availableColumns
        )}
      />
      <CloseIcon sx={SX_DELETE} onClick={() => onChange({})} />
    </Box>
  );
};

const InFuncNode = ({ exp, onChange }) => {
  const {
    funcValidFor,
    funcPairingType,
    funcProduces,
    availableColumns,
    columnEnumerations,
    funcEnumerations,
  } = useContext(EditorContext);
  const params = exp.params ?? [];
  const displayParams = [...params, {}];
  const constraints = inOperandConstraints(
    exp.value ?? {},
    displayParams,
    { pairingType: funcPairingType.IN, validFor: funcValidFor.IN ?? [] },
    { funcProduces, availableColumns, columnEnumerations, funcEnumerations }
  );

  const handleParamChange = (i, newValue) => {
    if (i < params.length) {
      if (isEmpty(newValue)) {
        onChange({ ...exp, params: params.filter((_, j) => j !== i) });
      } else {
        onChange({
          ...exp,
          params: params.map((v, j) => (j === i ? newValue : v)),
        });
      }
    } else {
      onChange({ ...exp, params: [...params, newValue] });
    }
  };

  return (
    <Box sx={{ ...NODE_SX, alignItems: "flex-start", p: 1, gap: 1 }}>
      <ExpressionNode
        exp={exp.value ?? {}}
        onChange={(v) => onChange({ ...exp, value: v })}
        acceptedTypes={constraints.value.acceptedTypes ?? []}
        requiredEnumerations={constraints.value.requiredEnumerations}
      />
      <Typography variant={"body2"} sx={{ fontWeight: 500, pt: 0.5 }}>
        {"in"}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {displayParams.map((param, i) => (
          <ExpressionNode
            key={i}
            exp={param}
            onChange={(v) => handleParamChange(i, v)}
            acceptedTypes={constraints.params[i].acceptedTypes ?? []}
            requiredEnumerations={constraints.params[i].requiredEnumerations}
          />
        ))}
      </Box>
      <CloseIcon sx={{ ...SX_DELETE, mt: 0.5 }} onClick={() => onChange({})} />
    </Box>
  );
};

const UnaryFuncNode = ({ exp, onChange }) => {
  const { unaryDisplay, funcValidFor } = useContext(EditorContext);
  const childAcceptedTypes = funcValidFor[exp.func] ?? [];
  return (
    <Box sx={{ ...NODE_SX, gap: 1, p: 1 }}>
      <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
        {unaryDisplay[exp.func]}
      </Typography>
      <ExpressionNode
        exp={exp.value ?? {}}
        onChange={(v) => onChange({ ...exp, value: v })}
        acceptedTypes={childAcceptedTypes}
      />
      <CloseIcon sx={SX_DELETE} onClick={() => onChange({})} />
    </Box>
  );
};

const NoParamsFuncNode = ({ exp, onChange }) => {
  const { noParamsDisplay } = useContext(EditorContext);
  return (
    <Box sx={NODE_SX}>
      <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
        {noParamsDisplay[exp.func]}
      </Typography>
      <CloseIcon sx={SX_DELETE} onClick={() => onChange({})} />
    </Box>
  );
};

const ColumnSlot = ({ name, validFor = [], onChange }) => {
  const { availableColumns } = useContext(EditorContext);
  const slotRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const matchingColumns = useMemo(
    () =>
      availableColumns.filter(
        (c) => !validFor.length || validFor.includes(c.type)
      ),
    [availableColumns, validFor]
  );

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: AGG_DRAG_TYPE,
      canDrop: (item) => !validFor.length || validFor.includes(item.type),
      drop: (item) => onChange(item.internalName),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [onChange, validFor]
  );

  const combinedRef = (node) => {
    slotRef.current = node;
    drop(node);
  };

  if (name) {
    const col = availableColumns.find((c) => c.internalName === name);
    const displayName = col?.displayName ?? name;
    return (
      <Box sx={NODE_SX}>
        <Typography variant={"body2"} color={"text.secondary"}>
          {"col"}
        </Typography>
        <Typography variant={"body2"}>{`"${displayName}"`}</Typography>
        <CloseIcon sx={SX_DELETE} onClick={() => onChange(null)} />
      </Box>
    );
  }

  return (
    <>
      <Box
        ref={combinedRef}
        onClick={() => setMenuOpen(true)}
        sx={{
          width: 20,
          height: 20,
          border: "1px solid",
          borderColor: isOver
            ? canDrop
              ? "primary.main"
              : "error.main"
            : "text.disabled",
          borderRadius: 0.5,
          cursor: "pointer",
          backgroundColor: isOver
            ? canDrop
              ? "action.hover"
              : "error.light"
            : "transparent",
          flexShrink: 0,
          opacity: isOver && !canDrop ? 0.5 : 1,
          "&:hover": { borderColor: "text.secondary" },
        }}
      />
      <Menu
        anchorEl={slotRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      >
        {matchingColumns.length === 0 ? (
          <MenuItem disabled>{"No matching columns"}</MenuItem>
        ) : (
          matchingColumns.map((c) => (
            <MenuItem
              key={c.internalName}
              dense
              onClick={() => {
                onChange(c.internalName);
                setMenuOpen(false);
              }}
            >
              {c.displayName ?? c.internalName}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

const ColumnFuncNode = ({ exp, onChange }) => {
  const { columnDisplay, columnValidFor } = useContext(EditorContext);
  const validFor = columnValidFor[exp.func] ?? [];
  return (
    <Box sx={{ ...NODE_SX, gap: 1, p: 1 }}>
      <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
        {columnDisplay[exp.func]}
      </Typography>
      <ColumnSlot
        name={exp.name ?? null}
        validFor={validFor}
        onChange={(name) => onChange({ ...exp, name })}
      />
      <CloseIcon sx={SX_DELETE} onClick={() => onChange({})} />
    </Box>
  );
};

const CaseFuncNode = ({
  exp,
  onChange,
  acceptedTypes,
  requiredEnumerations,
}) => {
  const {
    funcProduces,
    availableColumns,
    columnEnumerations,
    funcEnumerations,
  } = useContext(EditorContext);
  const displayCases = [...(exp.cases ?? []), { when: {}, then: {} }];
  const branchConstraints = passThroughConstraints(
    [...displayCases.map((c) => c.then), exp.otherwise ?? {}],
    acceptedTypes,
    requiredEnumerations,
    { funcProduces, availableColumns, columnEnumerations, funcEnumerations }
  );
  const otherwiseConstraints = branchConstraints[displayCases.length];

  const handleCaseChange = (i, field, newValue) => {
    if (i < exp.cases.length) {
      onChange({
        ...exp,
        cases: exp.cases.map((c, j) =>
          j === i ? { ...c, [field]: newValue } : c
        ),
      });
    } else {
      onChange({
        ...exp,
        cases: [...exp.cases, { when: {}, then: {}, [field]: newValue }],
      });
    }
  };

  const handleCaseDelete = (i) => {
    onChange({ ...exp, cases: exp.cases.filter((_, j) => j !== i) });
  };

  return (
    <Box
      sx={{
        ...NODE_SX,
        alignItems: "flex-start",
        flexDirection: "column",
        p: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Typography variant={"body2"} sx={{ fontWeight: 500, pt: 0.5 }}>
          {"Case"}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {displayCases.map((c, i) => {
            const isTrailing = i === (exp.cases ?? []).length;
            return (
              <Box key={i} sx={{ ...NODE_SX, gap: 1, alignItems: "center" }}>
                <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
                  {"When"}
                </Typography>
                <ExpressionNode
                  exp={c.when}
                  onChange={(v) => handleCaseChange(i, "when", v)}
                  acceptedTypes={["BOOL"]}
                />
                <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
                  {"Then"}
                </Typography>
                <ExpressionNode
                  exp={c.then}
                  onChange={(v) => handleCaseChange(i, "then", v)}
                  acceptedTypes={branchConstraints[i].acceptedTypes ?? []}
                  requiredEnumerations={
                    branchConstraints[i].requiredEnumerations
                  }
                />
                {!isTrailing && (
                  <CloseIcon
                    sx={SX_DELETE}
                    onClick={() => handleCaseDelete(i)}
                  />
                )}
              </Box>
            );
          })}
        </Box>
        <CloseIcon
          sx={{ ...SX_DELETE, mt: 0.5 }}
          onClick={() => onChange({})}
        />
      </Box>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, pl: 0.5 }}
      >
        <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
          {"Else"}
        </Typography>
        <ExpressionNode
          exp={exp.otherwise ?? {}}
          onChange={(v) => onChange({ ...exp, otherwise: v })}
          acceptedTypes={otherwiseConstraints.acceptedTypes ?? []}
          requiredEnumerations={otherwiseConstraints.requiredEnumerations}
        />
      </Box>
    </Box>
  );
};

ExpressionNode = ({
  exp,
  onChange,
  acceptedTypes = [],
  requiredEnumerations,
  literalMultiplier = 1,
  literalAffixes,
}) => {
  const {
    simpleFuncs,
    pairFuncs,
    unaryFuncs,
    noParamsFuncs,
    columnFuncs,
  } = useContext(EditorContext);
  if (isEmpty(exp))
    return (
      <EmptyNode
        onChange={onChange}
        acceptedTypes={acceptedTypes}
        requiredEnumerations={requiredEnumerations}
        literalMultiplier={literalMultiplier}
      />
    );
  if (isLiteral(exp))
    return (
      <LiteralNode
        exp={exp}
        onChange={onChange}
        requiredEnumerations={requiredEnumerations}
        literalMultiplier={literalMultiplier}
        literalAffixes={literalAffixes}
      />
    );
  if (exp.func === "VAR") return <VarNode exp={exp} onChange={onChange} />;
  if (columnFuncs.includes(exp.func))
    return <ColumnFuncNode exp={exp} onChange={onChange} />;
  if (simpleFuncs.includes(exp.func))
    return (
      <SimpleFuncNode
        exp={exp}
        onChange={onChange}
        acceptedTypes={acceptedTypes}
        requiredEnumerations={requiredEnumerations}
      />
    );
  if (pairFuncs.includes(exp.func))
    return <PairFuncNode exp={exp} onChange={onChange} />;
  if (exp.func === "IN") return <InFuncNode exp={exp} onChange={onChange} />;
  if (unaryFuncs.includes(exp.func))
    return <UnaryFuncNode exp={exp} onChange={onChange} />;
  if (noParamsFuncs.includes(exp.func))
    return <NoParamsFuncNode exp={exp} onChange={onChange} />;
  if (exp.func === "CASE")
    return (
      <CaseFuncNode
        exp={exp}
        onChange={onChange}
        acceptedTypes={acceptedTypes}
        requiredEnumerations={requiredEnumerations}
      />
    );
  return null;
};
ExpressionNode.displayName = "ExpressionNode";

const ColumnListItem = ({ column }) => {
  const ref = useRef(null);
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: AGG_DRAG_TYPE,
      item: {
        internalName: column.internalName,
        displayName: column.displayName,
        type: column.type,
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [column]
  );
  drag(ref);

  const colors = TYPE_COLORS[normalizeDataType(column.dataType)] ?? {};
  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        padding: "0.5em",
        borderRadius: 1,
        backgroundColor: "transparent",
        border: "1px solid transparent",
        color: "text.primary",
        fontSize: "0.875rem",
        fontWeight: 400,
        cursor: "grab",
        transition: "all 0.15s ease",
        userSelect: "none",
        gap: 1,
        opacity: isDragging ? 0.4 : 1,
        "&:hover": { backgroundColor: "action.hover", borderColor: "divider" },
        "&:active": { cursor: "grabbing" },
      }}
    >
      <Box sx={{ color: colors.text, display: "flex", alignItems: "center" }}>
        <DataTypeIcon type={normalizeDataType(column.dataType)} />
      </Box>
      <Box>{column.displayName}</Box>
    </Box>
  );
};

export const ExpressionEditor = React.memo(
  ({ availableColumns = [], expression = {}, onChange }) => {
    const theme = useTheme();
    const borderSx = {
      borderRadius: 2,
      border: `1px solid ${theme.brand.gray.gainsboro}`,
    };
    const aggregatorData = useAggregatorData();
    const columnEnumerations = useEditorEnumerations(availableColumns);
    const contextValue = useMemo(
      () => ({ availableColumns, ...aggregatorData, columnEnumerations }),
      [availableColumns, aggregatorData, columnEnumerations]
    );

    const {
      filterText,
      setFilterText,
      selectedDisplayTypes,
      typeCounts,
      filteredColumns,
      handleToggleType,
      handleSelectAnyMode,
      isAnyTypeMode,
    } = useColumnFilter(availableColumns);

    return (
      <EditorContext.Provider value={contextValue}>
        <Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              minHeight: 0,
            }}
          >
            <Typography
              variant={"subtitle2"}
              color={"text.secondary"}
              sx={SX_HEADING}
            >
              {"Columns"}
            </Typography>
            <ColumnFilterBar
              filterText={filterText}
              onFilterTextChange={setFilterText}
              selectedDisplayTypes={selectedDisplayTypes}
              onToggleType={handleToggleType}
              onSelectAnyMode={handleSelectAnyMode}
              isAnyTypeMode={isAnyTypeMode}
              typeCounts={typeCounts}
              totalCount={availableColumns.length}
              filteredCount={filteredColumns.length}
            />
            <Box
              sx={{
                ...borderSx,
                width: 300,
                flex: 1,
                minHeight: 288,
                overflowY: "auto",
                p: 1,
              }}
            >
              {filteredColumns.map((column) => (
                <ColumnListItem
                  key={
                    column.sourceTable
                      ? `${column.sourceTable}:${column.internalName}`
                      : column.internalName
                  }
                  column={column}
                />
              ))}
              {filteredColumns.length === 0 && (
                <Typography
                  variant={"body2"}
                  color={"text.secondary"}
                  sx={{ textAlign: "center", py: 2 }}
                >
                  {availableColumns.length === 0
                    ? "No columns available"
                    : "No columns match your filters"}
                </Typography>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              minHeight: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
              <Typography
                variant={"subtitle2"}
                color={"text.secondary"}
                sx={SX_HEADING}
              >
                {"Expression"}
              </Typography>
              <Typography variant={"caption"} color={"text.disabled"}>
                {
                  "The □ (Square) symbol is a placeholder for an expression — drag a column onto it to use that column’s value, or click it to get a list of functions."
                }
              </Typography>
            </Box>
            <Box
              sx={{
                ...borderSx,
                flex: 1,
                minHeight: 288,
                overflowY: "auto",
                overflowX: "auto",
                p: 2,
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <ExpressionNode exp={expression} onChange={onChange} />
            </Box>
          </Box>
        </Box>
      </EditorContext.Provider>
    );
  }
);
ExpressionEditor.displayName = "ExpressionEditor";
